import React, { useEffect, useRef, useState } from "react";

const AddStudent = ({ onStudentAdded }) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    grade: "",
    referredBy: "",
    referredReason: "",
    studentSupportStatus: [],
  });
  const [supportOpen, setSupportOpen] = useState(false);
  const supportRef = useRef(null);
  useEffect(() => {
    const handler = (e) => {
      if (!supportRef.current) return;
      if (!supportRef.current.contains(e.target)) setSupportOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("token");
      const apiUrl = process.env.REACT_APP_API_URL || "";
      const res = await fetch(`${apiUrl.replace(/\/$/, "")}/students`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to add student");
      setSuccess("Student added!");
      setForm({
        firstName: "",
        lastName: "",
        grade: "",
        referredBy: "",
        referredReason: "",
        studentSupportStatus: [],
      });
      if (onStudentAdded) onStudentAdded(data.student);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        maxWidth: 400,
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: 18,
      }}
    >
      <h2>Add New Student</h2>
      <input
        name="firstName"
        type="text"
        placeholder="First Name"
        value={form.firstName}
        onChange={handleChange}
        required
        style={{
          padding: 12,
          fontSize: 16,
          borderRadius: 4,
          border: "1px solid #ccc",
        }}
      />
      <input
        name="lastName"
        type="text"
        placeholder="Last Name"
        value={form.lastName}
        onChange={handleChange}
        required
        style={{
          padding: 12,
          fontSize: 16,
          borderRadius: 4,
          border: "1px solid #ccc",
        }}
      />
      <input
        name="grade"
        type="text"
        placeholder="Grade"
        value={form.grade}
        onChange={handleChange}
        required
        style={{
          padding: 12,
          fontSize: 16,
          borderRadius: 4,
          border: "1px solid #ccc",
        }}
      />
      <input
        name="referredBy"
        type="text"
        placeholder="Referred By (optional)"
        value={form.referredBy}
        onChange={handleChange}
        style={{
          padding: 12,
          fontSize: 16,
          borderRadius: 4,
          border: "1px solid #ccc",
        }}
      />
      <label
        htmlFor="referredReason"
        style={{ fontSize: 12, color: "#334155" }}
      >
        Reason for Referral (optional)
      </label>
      <select
        id="referredReason"
        name="referredReason"
        value={form.referredReason}
        onChange={handleChange}
        style={{
          padding: 12,
          fontSize: 16,
          borderRadius: 4,
          border: "1px solid #ccc",
          background: "#fff",
        }}
      >
        <option value="">-- Select Reason (optional) --</option>
        <option value="Academic">Academic</option>
        <option value="Behavioral">Behavioral</option>
        <option value="Social/Emotional">Social/Emotional</option>
        <option value="Family-related">Family-related</option>
        <option value="Attendance">Attendance</option>
      </select>

      <label style={{ fontSize: 12, color: "#334155" }}>
        Student Support Status (optional)
      </label>
      <div style={{ position: "relative" }} ref={supportRef}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setSupportOpen((o) => !o)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setSupportOpen((o) => !o);
            }
          }}
          style={{
            padding: 12,
            fontSize: 16,
            borderRadius: 4,
            border: "1px solid #ccc",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
        >
          <span
            style={{
              color: form.studentSupportStatus.length ? "#0f172a" : "#64748b",
            }}
          >
            {form.studentSupportStatus.length === 0
              ? "Select support status"
              : form.studentSupportStatus.join(", ")}
          </span>
          <span style={{ color: "#475569" }}>▾</span>
        </div>
        {supportOpen && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: 0,
              right: 0,
              background: "#fff",
              border: "1px solid #cbd5e1",
              borderRadius: 6,
              boxShadow: "0 6px 18px rgba(0,0,0,0.10)",
              marginTop: 4,
              padding: 8,
              zIndex: 20,
            }}
          >
            {[
              "IEP",
              "504",
              "RTI Tier 1",
              "RTI Tier 2",
              "RTI Tier 3",
              "Counseling only",
            ].map((opt) => {
              const checked = form.studentSupportStatus.includes(opt);
              return (
                <label
                  key={opt}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "4px 6px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setForm((prev) => {
                        const set = new Set(prev.studentSupportStatus);
                        if (isChecked) set.add(opt);
                        else set.delete(opt);
                        return {
                          ...prev,
                          studentSupportStatus: Array.from(set),
                        };
                      });
                    }}
                  />
                  <span>{opt}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={submitting}
        style={{
          padding: "14px 0",
          fontSize: 18,
          background: "#2563eb",
          color: "#fff",
          border: "none",
          borderRadius: 4,
          cursor: "pointer",
        }}
      >
        {submitting ? "Adding..." : "Add Student"}
      </button>
      {error && <div style={{ color: "red", marginTop: 10 }}>{error}</div>}
      {success && (
        <div style={{ color: "green", marginTop: 10 }}>{success}</div>
      )}
    </form>
  );
};

export default AddStudent;
